import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllRows } from '@/lib/fetchAllRows';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Skeleton } from '@/components/ui/skeleton';
import { Car, Banknote, Wrench, ShieldCheck, CalendarDays, FileCheck } from 'lucide-react';
import {
    AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, RadialBarChart, RadialBar,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const CONTRACT_COLORS: Record<string, string> = {
    Active: '#22c55e',
    Completed: '#3b82f6',
    Terminated: '#ef4444',
    Draft: '#f59e0b',
    'No Contract': '#94a3b8',
};

const COMPOSITION_COLORS = [
    'hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))',
    'hsl(var(--chart-4))', 'hsl(var(--chart-5))',
    '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6', '#f97316',
];

const formatCurrency = (amount: number, currency: string = 'GHS') =>
    new Intl.NumberFormat('en-GH', { style: 'currency', currency }).format(amount);

/**
 * One total per currency over vehicle_transactions rows, in first-appearance order. A row counts the
 * car's share — `allocated_amount ?? transactions.amount` — as the Vehicles list (VehicleValueDisplay)
 * and get_all_vehicle_transactions' COALESCE(vt.allocated_amount, t.amount) do: a fan-out
 * transaction split across a container's cars carries each car's share on allocated_amount.
 */
const totalsByCurrency = (rows: { allocated_amount: number | null; transactions: { amount: number; currency: string } | null }[]) => {
    const totals = new Map<string, number>();
    rows.forEach(r => {
        if (r.transactions) totals.set(r.transactions.currency, (totals.get(r.transactions.currency) || 0) + (r.allocated_amount ?? r.transactions.amount));
    });
    return Array.from(totals.entries()).map(([currency, amount]) => ({ currency, amount }));
};

const formatCompact = (amount: number) => {
    if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M`;
    if (amount >= 1_000) return `${(amount / 1_000).toFixed(1)}K`;
    return amount.toFixed(0);
};

interface FleetAnalytics {
    summary: {
        total_vehicles: number;
        total_purchase_cost: number;
        total_repaid: number;
        total_repayments: number;
        maintenance_spend: number;
        maintenance_count: number;
        registered_count: number;
        active_insurances: number;
        expired_insurances: number;
        total_contracts: number;
        insured_vehicles: number;
    };
    growth_timeline: { month: string; added: number; cumulative: number }[];
    contract_status: { name: string; value: number }[];
    make_model_composition: { name: string; value: number }[];
    year_distribution: { year: string; count: number }[];
}

// Custom tooltip for charts
const ChartTooltip = ({ active, payload, label, isCurrency }: any) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-lg border bg-background px-3 py-2 shadow-md">
            <p className="text-xs font-medium text-muted-foreground">{label || payload[0]?.name}</p>
            {payload.map((p: any, i: number) => (
                <p key={i} className="text-sm font-semibold" style={{ color: p.color || p.fill }}>
                    {p.name}: {isCurrency ? formatCurrency(p.value) : p.value.toLocaleString()}
                </p>
            ))}
        </div>
    );
};

// Custom legend for donut
const DonutLegend = ({ payload }: any) => (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
        {payload?.map((entry: any, i: number) => (
            <div key={i} className="flex items-center gap-1.5">
                <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-xs text-muted-foreground">{entry.value}</span>
            </div>
        ))}
    </div>
);

export default function VehiclesAnalytics() {
    const { data, isLoading } = useQuery<FleetAnalytics>({
        queryKey: ['fleet-analytics'],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_fleet_analytics');
            if (error) throw error;
            return data as FleetAnalytics;
        },
        staleTime: 5 * 60 * 1000,
    });

    // ONE SUM PER CURRENCY. The RPC's total_purchase_cost adds CIF paid in USD to landed cost paid in
    // GHS into one figure, and counts each payment whole on every car it is linked to. Fleet Value is
    // summed here instead, over the same rows (the cif_30, cif_70 and landed_cost links), one total per
    // currency and at each car's share: the cedi figure leads and each other currency is printed
    // beside it. A side read: while it loads, or if it fails, Fleet Value alone shows an em dash and
    // the rest of the page renders from the RPC.
    const { data: purchase, isError: purchaseFailed } = useQuery({
        queryKey: ['fleet-analytics-purchase-by-currency'],
        queryFn: async () => {
            // Every linked row, read to the server's exact count rather than until a page comes back
            // short: a short page also ends that loop when the server's Max rows is below the page
            // size, and the figure is then a part of the fleet. `id` is the primary key.
            const purchaseRows = await fetchAllRows({
                label: 'Fleet Value payments',
                orderedBy: 'id',
                page: (from, to) => supabase
                    .from('vehicle_transactions')
                    .select('id, allocated_amount, transactions!inner(amount, currency)', { count: 'exact' })
                    .in('transaction_type', ['cif_30', 'cif_70', 'landed_cost'])
                    .order('id')
                    .range(from, to),
                keyOf: (r) => r.id,
            });
            const totals = totalsByCurrency(purchaseRows);
            return {
                ghs: totals.find(o => o.currency === 'GHS')?.amount ?? 0,
                others: totals.filter(o => o.currency !== 'GHS'),
            };
        },
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading || !data) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
                </div>
                <div className="grid gap-6 lg:grid-cols-2">
                    <Skeleton className="h-80 rounded-2xl" />
                    <Skeleton className="h-80 rounded-2xl" />
                </div>
                <div className="grid gap-6 lg:grid-cols-2">
                    <Skeleton className="h-80 rounded-2xl" />
                    <Skeleton className="h-80 rounded-2xl" />
                </div>
            </div>
        );
    }

    const s = data.summary;
    const andOther = (totals: { currency: string; amount: number }[], per = 1) =>
        totals.map(o => `and ${formatCurrency(o.amount / per, o.currency)}`);
    const insuranceCoverage = s.total_vehicles > 0 ? Math.round((s.insured_vehicles / s.total_vehicles) * 100) : 0;
    const registrationRate = s.total_vehicles > 0 ? Math.round((s.registered_count / s.total_vehicles) * 100) : 0;

    // Radial data for insurance gauge
    const insuranceRadial = [
        { name: 'Coverage', value: insuranceCoverage, fill: insuranceCoverage >= 80 ? '#22c55e' : insuranceCoverage >= 50 ? '#f59e0b' : '#ef4444' },
    ];

    // Registration radial
    const registrationRadial = [
        { name: 'Registered', value: registrationRate, fill: '#3b82f6' },
    ];

    return (
        <div className="space-y-6">
            {/* Row 1: Hero KPI Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <StatCard
                    title="Fleet Size"
                    value={s.total_vehicles}
                    description="Total number of vehicles in your fleet"
                    subtitle={`${s.registered_count} registered · ${s.total_vehicles - s.registered_count} unregistered`}
                />
                <StatCard
                    title="Fleet Value"
                    value={purchase ? <CurrencyDisplay amount={purchase.ghs} /> : '—'}
                    description="Total purchase cost of all vehicles"
                    subtitle={purchase ? [
                        ...andOther(purchase.others),
                        `Avg ${[formatCurrency(purchase.ghs / (s.total_vehicles || 1)), ...andOther(purchase.others, s.total_vehicles || 1)].join(' ')} per vehicle`,
                    ].join(' · ') : purchaseFailed ? 'Could not be read' : undefined}
                />
                <StatCard
                    title="Revenue Collected"
                    value={<CurrencyDisplay amount={s.total_repaid} />}
                    description="Total repayments received from all contracts"
                    subtitle={`${s.total_repayments.toLocaleString()} payments across ${s.total_contracts} contracts`}
                />
                <StatCard
                    title="Maintenance Spend"
                    value={<CurrencyDisplay amount={s.maintenance_spend} />}
                    description="Total maintenance expenditure"
                    subtitle={`${s.maintenance_count} service records · Avg ${formatCurrency(s.maintenance_spend / (s.maintenance_count || 1))}`}
                />
            </div>

            {/* Row 2: Fleet Growth + Contract Status */}
            <div className="grid gap-6 lg:grid-cols-5">
                <Card className="border-2 border-border rounded-2xl lg:col-span-3">
                    <CardHeader>
                        <CardTitle className="text-base font-semibold">Fleet Growth</CardTitle>
                        <CardDescription>Cumulative vehicle count over time</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={data.growth_timeline} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                                <XAxis
                                    dataKey="month"
                                    tick={{ fontSize: 11 }}
                                    className="text-muted-foreground"
                                    tickFormatter={(v) => {
                                        const [y, m] = v.split('-');
                                        return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m)-1]} '${y.slice(2)}`;
                                    }}
                                    interval="preserveStartEnd"
                                />
                                <YAxis tick={{ fontSize: 11 }} className="text-muted-foreground" width={40} />
                                <Tooltip content={<ChartTooltip />} />
                                <Area
                                    type="monotone"
                                    dataKey="cumulative"
                                    stroke="hsl(var(--chart-1))"
                                    strokeWidth={2.5}
                                    fill="url(#growthGradient)"
                                    name="Total Vehicles"
                                    dot={false}
                                    activeDot={{ r: 5, strokeWidth: 2 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                <Card className="border-2 border-border rounded-2xl lg:col-span-2">
                    <CardHeader>
                        <CardTitle className="text-base font-semibold">Contract Status</CardTitle>
                        <CardDescription>Distribution across {s.total_vehicles} vehicles</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={data.contract_status}
                                    cx="50%"
                                    cy="45%"
                                    innerRadius={65}
                                    outerRadius={100}
                                    paddingAngle={3}
                                    dataKey="value"
                                    strokeWidth={0}
                                >
                                    {data.contract_status.map((entry, i) => (
                                        <Cell key={i} fill={CONTRACT_COLORS[entry.name] || '#94a3b8'} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    content={({ active, payload }) => {
                                        if (!active || !payload?.length) return null;
                                        const d = payload[0];
                                        return (
                                            <div className="rounded-lg border bg-background px-3 py-2 shadow-md">
                                                <p className="text-sm font-semibold">{d.name}: {(d.value as number).toLocaleString()}</p>
                                            </div>
                                        );
                                    }}
                                />
                                <Legend content={<DonutLegend />} />
                                {/* Center label */}
                                <text x="50%" y="43%" textAnchor="middle" dominantBaseline="middle" className="fill-foreground text-2xl font-bold">
                                    {s.total_vehicles}
                                </text>
                                <text x="50%" y="51%" textAnchor="middle" dominantBaseline="middle" className="fill-muted-foreground text-xs">
                                    vehicles
                                </text>
                            </PieChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>

            {/* Row 3: Fleet Composition + Insurance & Registration */}
            <div className="grid gap-6 lg:grid-cols-5">
                <Card className="border-2 border-border rounded-2xl lg:col-span-3">
                    <CardHeader>
                        <CardTitle className="text-base font-semibold">Fleet Composition</CardTitle>
                        <CardDescription>Top vehicle makes & models</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={data.make_model_composition} layout="vertical" margin={{ left: 10, right: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 11 }} className="text-muted-foreground" />
                                <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 12 }} className="text-muted-foreground" />
                                <Tooltip content={<ChartTooltip />} />
                                <Bar dataKey="value" name="Vehicles" radius={[0, 6, 6, 0]} barSize={24}>
                                    {data.make_model_composition.map((_, i) => (
                                        <Cell key={i} fill={COMPOSITION_COLORS[i % COMPOSITION_COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                <Card className="border-2 border-border rounded-2xl lg:col-span-2">
                    <CardHeader>
                        <CardTitle className="text-base font-semibold">Coverage & Compliance</CardTitle>
                        <CardDescription>Insurance and registration health</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Insurance Gauge */}
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">Insurance Coverage</span>
                            </div>
                            <div className="flex items-center gap-4">
                                <ResponsiveContainer width={100} height={100}>
                                    <RadialBarChart
                                        cx="50%" cy="50%"
                                        innerRadius="70%" outerRadius="100%"
                                        startAngle={90} endAngle={-270}
                                        data={insuranceRadial}
                                        barSize={10}
                                    >
                                        <RadialBar
                                            dataKey="value"
                                            cornerRadius={10}
                                            background={{ fill: 'hsl(var(--muted))' }}
                                        />
                                        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="fill-foreground text-sm font-bold">
                                            {insuranceCoverage}%
                                        </text>
                                    </RadialBarChart>
                                </ResponsiveContainer>
                                <div className="space-y-1 text-sm">
                                    <div className="flex items-center gap-2">
                                        <div className="h-2 w-2 rounded-full bg-green-500" />
                                        <span className="text-muted-foreground">{s.insured_vehicles} vehicles covered</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="h-2 w-2 rounded-full bg-red-500" />
                                        <span className="text-muted-foreground">{s.total_vehicles - s.insured_vehicles} uninsured</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="h-2 w-2 rounded-full bg-amber-500" />
                                        <span className="text-muted-foreground">{s.expired_insurances} expired policies</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border-t" />

                        {/* Registration Gauge */}
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <FileCheck className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">Registration Status</span>
                            </div>
                            <div className="flex items-center gap-4">
                                <ResponsiveContainer width={100} height={100}>
                                    <RadialBarChart
                                        cx="50%" cy="50%"
                                        innerRadius="70%" outerRadius="100%"
                                        startAngle={90} endAngle={-270}
                                        data={registrationRadial}
                                        barSize={10}
                                    >
                                        <RadialBar
                                            dataKey="value"
                                            cornerRadius={10}
                                            background={{ fill: 'hsl(var(--muted))' }}
                                        />
                                        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="fill-foreground text-sm font-bold">
                                            {registrationRate}%
                                        </text>
                                    </RadialBarChart>
                                </ResponsiveContainer>
                                <div className="space-y-1 text-sm">
                                    <div className="flex items-center gap-2">
                                        <div className="h-2 w-2 rounded-full bg-blue-500" />
                                        <span className="text-muted-foreground">{s.registered_count} registered</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="h-2 w-2 rounded-full bg-slate-400" />
                                        <span className="text-muted-foreground">{s.total_vehicles - s.registered_count} unregistered</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Row 4: Year Distribution */}
            <Card className="border-2 border-border rounded-2xl">
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="text-base font-semibold">Manufacture Year Distribution</CardTitle>
                        <CardDescription>Fleet age composition</CardDescription>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CalendarDays className="h-4 w-4" />
                        <span>
                            {data.year_distribution.length > 0 && 
                                `${data.year_distribution[0].year} – ${data.year_distribution[data.year_distribution.length - 1].year}`
                            }
                        </span>
                    </div>
                </CardHeader>
                <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={data.year_distribution} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                            <XAxis dataKey="year" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                            <YAxis tick={{ fontSize: 11 }} className="text-muted-foreground" width={40} />
                            <Tooltip content={<ChartTooltip />} />
                            <Bar dataKey="count" name="Vehicles" fill="hsl(var(--chart-2))" radius={[6, 6, 0, 0]} barSize={48} />
                        </BarChart>
                    </ResponsiveContainer>
                </CardContent>
            </Card>
        </div>
    );
}
